import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Projekt, ProjektStatus, AuftragKurz } from '../types';
import { RegieberichtKarte } from './RegieberichtForm';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_COLORS: Record<ProjektStatus, string> = {
  Geplant: 'var(--ink-2)',
  Aktiv: 'var(--green-900)',
  Pausiert: 'var(--gold)',
  Abgeschlossen: 'var(--green-500)',
  Abgebrochen: 'var(--red)',
};

interface ProjektListProps {
  authToken: string;
  /** Rolle des angemeldeten Benutzers. Der projekte-Tab ist auch für
   *  Projektmitarbeiter sichtbar; die eingebettete RegieberichtKarte darf
   *  ihnen deshalb keine Abrechnungs-Aktionen anbieten. */
  userRolle?: string;
}

const ProjektList: React.FC<ProjektListProps> = ({ authToken, userRolle }) => {
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ projekt_nr: '', bezeichnung: '', beschreibung: '', budget_stunden: '', budget_gesamt: '' });
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [kalkulation, setKalkulation] = useState<Record<string, unknown> | null>(null);
  const [auftraege, setAuftraege] = useState<AuftragKurz[]>([]);
  // Getrennt vom Seiten-weiten `fehler` (der die ganze Liste per Early-Return
  // ersetzt): ein Fehler beim Aufklappen EINER Zeile darf nicht die bereits
  // erfolgreich geladene Projektliste verdraengen. pid haelt fest, zu welcher
  // Zeile der Fehler gehoert, damit er unter genau dieser Zeile erscheint.
  const [detailFehler, setDetailFehler] = useState<{ pid: number; msg: string } | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setFehler(null);
    axios.get<Projekt[]>(`${API_URL}/api/projekte`)
      .then(r => setProjekte(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Projekte konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/projekte`, {
        ...form,
        budget_stunden: form.budget_stunden ? parseFloat(form.budget_stunden) : null,
        budget_gesamt: form.budget_gesamt ? parseFloat(form.budget_gesamt) : null,
      });
      setShowForm(false);
      setForm({ projekt_nr: '', bezeichnung: '', beschreibung: '', budget_stunden: '', budget_gesamt: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const loadKalkulation = async (pid: number) => {
    if (selectedId === pid) { setSelectedId(null); setKalkulation(null); setAuftraege([]); return; }
    setDetailFehler(null);
    try {
      const [kalkRes, auftraegeRes] = await Promise.all([
        axios.get(`${API_URL}/api/projekte/${pid}/kalkulation`),
        axios.get<AuftragKurz[]>(`${API_URL}/api/projekte/${pid}/auftraege`),
      ]);
      setKalkulation(kalkRes.data);
      setAuftraege(auftraegeRes.data);
      setSelectedId(pid);
    } catch (err: unknown) {
      // selectedId/kalkulation/auftraege bewusst unangetastet: schlaegt das
      // Aufklappen einer Zeile fehl, waehrend eine ANDERE bereits erfolgreich
      // offen ist, bleibt deren Ansicht bestehen statt mitzukollabieren.
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setDetailFehler({ pid, msg: `Baustellen-Details konnten nicht geladen werden: ${msg}` });
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Lade Projekte…</div>;

  if (fehler) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)', borderRadius: 0,
          fontSize: 13, color: 'var(--red)',
        }}>
          <span>{fehler}</span>
          <button
            className="k-btn k-btn--danger k-btn--sm"
            onClick={load}
            style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Projekte</h2>
        <button
          className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Abbrechen' : '+ Neu'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <input required placeholder="Projektnummer *" value={form.projekt_nr} onChange={e => setForm(f => ({ ...f, projekt_nr: e.target.value }))} style={{ padding: 8 }} />
            <input required placeholder="Bezeichnung *" value={form.bezeichnung} onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="Budget Stunden" type="number" value={form.budget_stunden} onChange={e => setForm(f => ({ ...f, budget_stunden: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="Budget gesamt €" type="number" value={form.budget_gesamt} onChange={e => setForm(f => ({ ...f, budget_gesamt: e.target.value }))} style={{ padding: 8 }} />
            <textarea placeholder="Beschreibung" value={form.beschreibung} onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} style={{ padding: 8, gridColumn: '1 / -1' }} rows={2} />
          </div>
          <button type="submit" className="k-btn k-btn--primary" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </form>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bone-3)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nr</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Bezeichnung</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Budget h</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Budget €</th>
            <th style={{ padding: '8px 12px', textAlign: 'center' }}>Kalkulation</th>
          </tr>
        </thead>
        <tbody>
          {projekte.map(p => (
            <React.Fragment key={p.projekt_id}>
              <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{p.projekt_nr}</td>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{p.bezeichnung}</td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{ background: STATUS_COLORS[p.status], color: 'var(--bone)', padding: '2px 8px', borderRadius: 0, fontSize: 12 }}>
                    {p.status}
                  </span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{p.budget_stunden ?? '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{p.budget_gesamt != null ? `${p.budget_gesamt.toFixed(2)} €` : '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <button className="k-btn k-btn--ghost k-btn--sm" onClick={() => loadKalkulation(p.projekt_id)}>
                    {selectedId === p.projekt_id ? 'Schließen' : 'Soll/Ist'}
                  </button>{' '}
                  <button
                    className="k-btn k-btn--ghost k-btn--sm"
                    onClick={() => setAkteId(akteId === p.projekt_id ? null : p.projekt_id)}
                  >
                    {akteId === p.projekt_id ? 'Akte schließen' : 'Akte'}
                  </button>
                </td>
              </tr>
              {selectedId === p.projekt_id && kalkulation && (
                <tr>
                  <td colSpan={6} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                    <div style={{ marginBottom: 16 }}>
                      {`Kalkulation — Aufträge: ${kalkulation.anzahl_auftraege} | Stunden: ${(kalkulation.ist_stunden as number).toFixed(1)} / ${kalkulation.budget_stunden ?? '—'} h (${(kalkulation.ist_stunden_eur as number).toFixed(2)} €) | Material: ${(kalkulation.ist_material as number).toFixed(2)} / ${kalkulation.budget_material ?? '—'} € | Gesamt-Budget: ${kalkulation.budget_gesamt ?? '—'} €`}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Aufträge an dieser Baustelle</div>
                      {auftraege.length === 0 ? (
                        <p style={{ color: 'var(--ink-2)', margin: 0 }}>Keine Aufträge an dieser Baustelle.</p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '4px 8px', textAlign: 'left' }}>Nr</th>
                              <th style={{ padding: '4px 8px', textAlign: 'left' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {auftraege.map(a => (
                              <tr key={a.auftrag_id} style={{ borderTop: '1px solid var(--bone-3)' }}>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{a.auftrag_nr}</td>
                                <td style={{ padding: '4px 8px' }}>{a.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {detailFehler && detailFehler.pid === p.projekt_id && (
                <tr>
                  <td colSpan={6} style={{ padding: '12px 24px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                      background: 'var(--bone-2)', border: '1px solid var(--bone-3)', borderRadius: 0,
                      fontSize: 13, color: 'var(--red)',
                    }}>
                      <span>{detailFehler.msg}</span>
                      <button
                        className="k-btn k-btn--danger k-btn--sm"
                        onClick={() => loadKalkulation(p.projekt_id)}
                        style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                      >
                        Erneut versuchen
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {akteId === p.projekt_id && (
                <tr>
                  <td colSpan={6} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                    <div style={{ marginBottom: 16 }}>
                      <RegieberichtKarte projektId={p.projekt_id} authToken={authToken} userRolle={userRolle} />
                    </div>
                    <AktivitaetenBlock entitaetTyp="projekt" entitaetId={p.projekt_id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {projekte.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Keine Projekte vorhanden.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProjektList;
